import { CheckoutFunction, CheckoutOptions } from '../../../hooks/payment/useOrderCheckout';
import { PaymentIntent, PaymentIntentResult, Stripe } from '@stripe/stripe-js';
import { Order, OrderData, PaymentResult } from '../../../services/exports/Interfaces';
import { PaymentStatus } from '../../../services/exports/Constants';
import {
  ConfirmPaymentFunction,
  GenerateOrderReturnUrlFunction,
  OnOrderPaymentErrorFunction,
} from '../../../hooks/payment/useCheckout';

export const stripeResultCaster = (result: PaymentIntentResult): PaymentResult => ({
  status: result.error ? PaymentStatus.Failed : PaymentStatus.Succeeded,
  error: result.error,
  result: result.paymentIntent,
});

/** What an intent may read after its next action for the payment to count as made. */
const PAID_AFTER_NEXT_ACTION: PaymentIntent.Status[] = ['succeeded', 'processing', 'requires_capture'];

/**
 * The Stripe Elements checkout's 402: the backend confirmed the intent with the ConfirmationToken and
 * the customer has something left to do — a 3DS challenge (in the page) or a bank's page (a redirect
 * back to the order). Stripe.js runs it. Only a paid (or settling) intent is a payment; anything else
 * it comes back as — still wanting a method or another action, cancelled — is the challenge failed or
 * abandoned.
 */
export async function handleStripeNextAction(stripe: Stripe, paymentIntent: PaymentIntent): Promise<PaymentResult> {
  const result = await stripe.handleNextAction({ clientSecret: paymentIntent.client_secret });

  if (result.error) {
    return { status: PaymentStatus.Failed, error: result.error, result: null };
  }

  if (!PAID_AFTER_NEXT_ACTION.includes(result.paymentIntent?.status)) {
    return {
      status: PaymentStatus.Failed,
      error: result.paymentIntent?.last_payment_error ?? null,
      result: result.paymentIntent ?? null,
    };
  }

  return { status: PaymentStatus.Succeeded, error: null, result: result.paymentIntent };
}

export interface PayButtonProps {
  title?: string;
  showTerms?: boolean;
  className?: string;
}

export interface ChildPayButtonProps extends PayButtonProps {
  title: string;
  loading: boolean;
  disabled: boolean;
  silentlyDisabled: boolean;
  setLoading: (value: boolean) => void;
  onClick: (
    confirmPayment: ConfirmPaymentFunction,
    paymentIntent?: PaymentIntent,
    customCheckout?: CheckoutFunction,
    options?: CheckoutOptions,
    /** Fields a gateway button adds to the order payload (the Payment Element's confirmed type). */
    extraData?: Partial<OrderData>,
  ) => Promise<boolean>;
  onPaymentSuccess: (order: Order) => void;
  onPaymentError: OnOrderPaymentErrorFunction;
  generateReturnUrl: GenerateOrderReturnUrlFunction;
  /**
   * The parent's checkout — order or gift card. A gateway button that has to wrap the checkout
   * call (Spreedly injects its tokens into the payload) must use THIS one rather than reach for
   * useOrderCheckout itself, or a gift-card purchase would run the order checkout.
   */
  checkout?: CheckoutFunction;
}
